import React from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import type { PolicySubject } from '../../domain/policies/policyPacks';
import type { PolicyAliasDefinition } from '../../domain/policies/policyPacks';
import type { PackDefinition } from '../../domain/policies/policyPacks';
import { resolveInvestigationAlignmentFromAlias } from '../../domain/reportContext/investigationAlignment';
import { resolveClaimsRoiFromAlias } from '../../domain/reportContext/claimsRoiAlignment';
import { resolveSafetyControlReviewFromAlias } from '../../domain/reportContext/safetyControlReview';
import type { PolicyCreateData } from '../../services/policiesApi';
import InvestigationAlignmentCard from '../safetyReports/InvestigationAlignmentCard';
import ClaimsRoiAlignmentCard from '../safetyReports/ClaimsRoiAlignmentCard';
import SafetyControlReviewCard from '../safetyReports/SafetyControlReviewCard';
import { describePolicyUseCase, formatPolicySubjectLabel, getWarehouseEdgeSupport, effectiveAlertLinesFromForm } from './policyConfigPresets';

const hasScheduleWindows = (windows: any): boolean =>
  Array.isArray(windows) && windows.some((w: any) => w && (w.day != null || w.start || w.end));

const isTimeBasedSchedule = (schedule: any): boolean => {
  if (!schedule) return false;
  if (schedule.enabled === false) return false;
  const hasLegacy =
    (Array.isArray(schedule.days) && schedule.days.length > 0) ||
    Boolean(schedule.start_time) ||
    Boolean(schedule.end_time);
  const hasExceptions = Array.isArray(schedule.exceptions) && schedule.exceptions.length > 0;
  return schedule.enabled === true || hasScheduleWindows(schedule.windows) || hasLegacy || hasExceptions;
};

const summarizeSchedule = (schedule: any): string => {
  if (!isTimeBasedSchedule(schedule)) return 'Always active';
  const timezone = String(schedule?.timezone || 'UTC');
  const windowsCount = Array.isArray(schedule?.windows) ? schedule.windows.length : 0;
  return windowsCount > 0 ? `Scheduled • ${windowsCount} window${windowsCount === 1 ? '' : 's'} • ${timezone}` : `Scheduled • ${timezone}`;
};

const summarizeActions = (actions: any): string => {
  const list = Array.isArray(actions) ? actions.filter((a: any) => a && a.enabled !== false) : [];
  const labels = [
    list.some((a: any) => String(a?.type) === 'odoo_ticket') ? 'Odoo' : null,
    list.some((a: any) => String(a?.type) === 'email.notify') ? 'Email' : null,
    list.some((a: any) => String(a?.type) === 'sms.notify') ? 'SMS' : null,
    list.some((a: any) => String(a?.type) === 'genetec.notify') ? 'Genetec' : null,
    list.some((a: any) => String(a?.type) === 'webhook.call') ? 'Webhook' : null,
  ].filter(Boolean) as string[];
  return labels.length ? labels.join(', ') : 'None';
};

export interface PolicyReviewStepProps {
  selectedPackGroup: PackDefinition | null;
  selectedPolicyOption: PolicyAliasDefinition | undefined;
  selectedPolicySubject: PolicySubject;
  formData: Partial<PolicyCreateData>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<PolicyCreateData>>>;

  selectedCameraId: string;
  selectedCameraProfileId?: 'A' | 'B' | 'C' | 'D' | '';
  selectedZoneIds: string[];
  selectedLineId: string;
  allowsWholeViewScope: boolean;
  scopeReady: boolean;
  scopeRequirements: string[];

  postCreateAction: 'draft' | 'activate' | 'activate_deploy';
  setPostCreateAction: (action: 'draft' | 'activate' | 'activate_deploy') => void;

  saving: boolean;
  editingPolicy: any | null;
}

const PolicyReviewStep: React.FC<PolicyReviewStepProps> = ({
  selectedPackGroup,
  selectedPolicyOption,
  selectedPolicySubject,
  formData,
  setFormData,
  selectedCameraId,
  selectedCameraProfileId,
  selectedZoneIds,
  selectedLineId,
  allowsWholeViewScope,
  scopeReady,
  scopeRequirements,
  postCreateAction,
  setPostCreateAction,
  saving,
  editingPolicy,
}) => {
  const isWorkerDownPolicy = String(formData.policy_type || '') === 'worker_down';
  const policyUseCase = describePolicyUseCase(
    selectedPolicyOption?.alias,
    String(formData.policy_type || 'zone_intrusion'),
  );
  const investigationAlignment = resolveInvestigationAlignmentFromAlias(selectedPolicyOption?.alias);
  const claimsRoiAlignment = resolveClaimsRoiFromAlias(selectedPolicyOption?.alias);
  const safetyControlReview = resolveSafetyControlReviewFromAlias(selectedPolicyOption?.alias);

  return (
    <>
      {/* Review summary */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Review
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip size="small" label={`Pack: ${selectedPackGroup?.label || 'Core Safety'}`} />
          <Chip size="small" color="primary" variant="outlined" label={`Policy: ${selectedPolicyOption?.label || 'Zone Intrusion'}`} />
          <Chip size="small" label={`Subject: ${formatPolicySubjectLabel(selectedPolicySubject)}`} />
          <Chip size="small" label={`Camera: ${selectedCameraId || 'Not selected'}`} />
          <Chip size="small" label={`Profile: ${selectedCameraProfileId || 'Unknown'}`} />
          <Chip
            size="small"
            label={
              formData.policy_type === 'line_crossing'
                ? `Line: ${selectedLineId || 'Not selected'}`
                : `Zones: ${selectedZoneIds.length}${allowsWholeViewScope && selectedZoneIds.length === 0 ? ' (whole view)' : ''}`
            }
          />
          <Chip size="small" label={`Hours: ${summarizeSchedule((formData as any).schedule)}`} />
          <Chip size="small" label={`Severity: ${String((formData as any).alert?.severity || 'medium')}`} />
          <Chip size="small" label={`Immediate: ${(formData as any).alert?.enabled === false ? 'Off' : 'On'}`} />
          <Chip size="small" label={`Pattern Alert: ${(formData as any).pattern?.enabled === true ? 'On' : 'Off'}`} />
          <Chip size="small" label={`Actions: ${summarizeActions((formData as any).actions)}`} />
        </Box>
        <Box sx={{ mt: 1.5 }} data-testid="effective-alert-readout">
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Effective Alert configuration</Typography>
          {effectiveAlertLinesFromForm(formData).map((line) => (
            <Typography key={line} variant="body2">{line}</Typography>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Active hours and notification targets are configured in Step 3 and summarized here before create.
        </Typography>
      </Paper>

      {policyUseCase && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Policy purpose
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {policyUseCase}
          </Typography>
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <InvestigationAlignmentCard
          alignment={investigationAlignment}
          title="Investigation alignment preview"
          compact
          testId="policy-review-investigation-alignment"
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <ClaimsRoiAlignmentCard
          alignment={claimsRoiAlignment}
          title="Claims / ROI alignment preview"
          compact
          testId="policy-review-claims-roi-alignment"
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <SafetyControlReviewCard
          alignment={safetyControlReview}
          title="Safety control review preview"
          compact
          testId="policy-review-safety-control-review"
        />
      </Box>

      {(() => {
        const meta = getWarehouseEdgeSupport(
          String(formData.policy_type || 'zone_intrusion'),
          selectedCameraProfileId,
        );
        if (!meta.message && meta.support === 'supported') return null;
        const sev =
          meta.support === 'not_supported'
            ? 'error'
            : meta.support === 'supported'
              ? 'info'
              : 'warning';
        return (
          <Alert severity={sev as any} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Runtime compatibility review
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {meta.message || 'Review camera profile compatibility before create/deploy.'}
            </Typography>
          </Alert>
        );
      })()}

      {!scopeReady && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You still need to finish configuration before creating this policy: {scopeRequirements.join(' · ')}.
        </Alert>
      )}

      {isWorkerDownPolicy && !editingPolicy && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Worker Down is still in a staged launch path. New Worker Down policies are limited to draft creation until runtime, evidence, and rollout gates are complete.
        </Alert>
      )}

      <TextField
        fullWidth
        label="Policy Name"
        value={formData.name || ''}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        sx={{ mb: 2 }}
        required
        placeholder={selectedPolicyOption?.label || 'Enter a unique policy name'}
        helperText="Required. Choose a unique operator-facing label shown in policy cards and incidents."
      />

      <TextField
        fullWidth
        label="Description (optional)"
        value={formData.description || ''}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        multiline
        rows={2}
        sx={{ mb: 2 }}
      />

      {/* Enabled toggle + post-create action */}
      <FormControlLabel
        control={
          <Checkbox
            checked={formData.enabled !== false}
            onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
            disabled={saving}
          />
        }
        label="Enabled"
      />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="policy-save-not-measuring">
        Saving this policy does not start measuring.
      </Typography>

      {!editingPolicy && (
        <Box mt={2}>
          <Divider sx={{ mb: 2 }} />
          <FormControl component="fieldset">
            <FormLabel component="legend">After Create</FormLabel>
            <RadioGroup value={postCreateAction} onChange={(e) => setPostCreateAction(e.target.value as any)}>
              <FormControlLabel
                value="activate_deploy"
                control={<Radio />}
                label="Create → Activate → Deploy"
                disabled={saving || isWorkerDownPolicy}
              />
              <FormControlLabel value="activate" control={<Radio />} label="Create → Activate" disabled={saving || isWorkerDownPolicy} />
              <FormControlLabel value="draft" control={<Radio />} label="Create Draft" disabled={saving} />
            </RadioGroup>
          </FormControl>
        </Box>
      )}
    </>
  );
};

export default PolicyReviewStep;
